import { formatDate } from '@angular/common';
import { isAgronaveModel } from 'app/utils/device-model.utils';
import {
  FieldNotebookEntry,
  FieldNotebookProduct,
  FieldNotebookStatus,
} from 'app/core/interface/field-notebook';
import { Field } from 'app/core/interface/entity';

const MINUTES_PER_HOUR = 60;
const MS_PER_DAY = 86400000;
const DISPLAY_DATE_FORMAT = 'dd/MM/yyyy';
export const EXPORT_COLUMNS = [
  'use_type',
  'product',
  'authorization_number',
  'application_date',
  'start_time',
  'dose',
  'dose_unit',
  'parcel_reference',
  'treated_area_ha',
  'crop',
  'eppo_code',
  'bbch',
  'timezone',
  'field',
  'total_quantity',
  'quantity_unit',
  'target',
  'operator',
  'machine',
  'origin',
  'status',
  'confirmed_at',
] as const;
export type ExportRow = Record<
  (typeof EXPORT_COLUMNS)[number],
  string | number
>;
export function languageCode(language?: string): 'ptBR' | 'en' | 'es' | 'it' {
  if (language === 'en' || language === 'es' || language === 'it') {
    return language;
  }
  return 'ptBR';
}
/** Uses each boundary's local offset, including DST changes within the period. */
export function localIso(date: Date): string {
  const pad = (value: number, width = 2) => String(value).padStart(width, '0');
  const offset = -date.getTimezoneOffset();
  let sign = '+';
  if (offset < 0) {
    sign = '-';
  }
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${pad(date.getMilliseconds(), 3)}${sign}${pad(Math.floor(Math.abs(offset) / MINUTES_PER_HOUR))}:${pad(Math.abs(offset) % MINUTES_PER_HOUR)}`;
}
export function periodBounds(
  from: string,
  to: string,
): { from: string; to: string } {
  const start = new Date(`${from}T00:00:00.000`);
  const end = new Date(`${to}T23:59:59.999`);
  if (
    !Number.isFinite(start.getTime()) ||
    !Number.isFinite(end.getTime()) ||
    start > end
  ) {
    throw new Error('invalid_period');
  }
  return { from: localIso(start), to: localIso(end) };
}
/** Recorded offsets are authoritative; an IANA label is a fallback for UTC instants. */
export function entryDateTime(
  value: string,
  timezone?: string,
): { date: string; time: string } {
  if (!value || !Number.isFinite(Date.parse(value))) {
    return { date: '', time: '' };
  }
  if (/[+-]\d{2}:\d{2}$/.test(value) || !timezone) {
    return { date: value.slice(0, 10), time: value.slice(11, 16) };
  }
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date(value));
    const part = (type: string) =>
      parts.find((item) => item.type === type)?.value ?? '';
    return {
      date: `${part('year')}-${part('month')}-${part('day')}`,
      time: `${part('hour')}:${part('minute')}`,
    };
  } catch {
    return { date: value.slice(0, 10), time: value.slice(11, 16) };
  }
}
/** Uses the application's DatePipe format after resolving the recorded entry day. */
export function displayEntryDateTime(
  value: string,
  timezone?: string,
): { date: string; time: string } {
  const result = entryDateTime(value, timezone);
  if (result.date) {
    result.date = formatDate(result.date, DISPLAY_DATE_FORMAT, 'en');
  }
  return result;
}

/** Selects a translated count form; zero and all values other than one use plural. */
export function countKey(key: string, count: number): string {
  if (count === 1) {
    return `field-notebook.${key}-one`;
  }
  return `field-notebook.${key}-other`;
}

/** Selects due wording independently from the signed count used for ordering. */
export function dueDayMessage(days: number): { key: string; count: number } {
  if (days === 0) {
    return { key: 'field-notebook.due-today', count: 0 };
  }
  if (days < 0) {
    return {
      key: countKey('days-overdue', Math.abs(days)),
      count: Math.abs(days),
    };
  }
  return { key: countKey('days-left', days), count: days };
}

/** Calendar ordinals avoid elapsed-hour rounding across DST and partial days. */
function calendarDay(date: Date): number {
  return (
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / MS_PER_DAY
  );
}

export function doseFromQuantity(
  quantity?: number,
  area?: number,
): number | undefined {
  if (
    quantity !== undefined &&
    quantity !== null &&
    Number.isFinite(quantity) &&
    quantity >= 0 &&
    Number.isFinite(area) &&
    area > 0
  ) {
    return quantity / area;
  }
  return undefined;
}
export function origin(entry: FieldNotebookEntry): string {
  if (entry.source.type === 'manual') {
    return 'manual';
  }
  const model = entry.source.equipment ?? '';
  if (model === 'Agronave') {
    return 'isoView';
  }
  if (isAgronaveModel(model)) {
    return 'agroNave';
  }
  const equipment = model.toLowerCase();
  const origins: Record<string, string> = {
    agronave: 'agroNave',
    'agro-nave': 'agroNave',
    isoview: 'isoView',
    'iso-view': 'isoView',
    sprayrate: 'SprayRate',
    'spray-rate': 'SprayRate',
    isobus: 'ISOBUS',
  };
  return origins[equipment] ?? 'task';
}
export function productRows(entries: FieldNotebookEntry[]) {
  return entries.flatMap((entry) => {
    let products = entry.products;
    if (!products.length) {
      products = [{ name: '' }];
    }
    return products.map((product, index) => ({ entry, product, index }));
  });
}
export function buildExportRows(
  entries: FieldNotebookEntry[],
  confirmed = true,
  ready = false,
): ExportRow[] {
  return entries
    .filter(
      (entry) =>
        (confirmed && entry.status === 'confirmed') ||
        (ready && entry.status === 'ready'),
    )
    .flatMap((entry) =>
      entry.products.map((product) => {
        const date = entryDateTime(entry.startedAt, entry.timezone);
        let confirmedAt = '';
        if (entry.confirmedAt !== undefined) {
          confirmedAt = new Date(entry.confirmedAt).toISOString();
        }
        return {
          use_type: entry.useType,
          product: product.name,
          authorization_number: product.authorizationNumber ?? '',
          application_date: date.date,
          start_time: date.time,
          dose: product.dose ?? '',
          dose_unit: product.doseUnit ?? '',
          parcel_reference: entry.field?.parcelReference ?? '',
          treated_area_ha: entry.treatedAreaHa ?? '',
          crop: entry.crop?.name ?? '',
          eppo_code: entry.crop?.eppoCode ?? '',
          bbch: entry.crop?.bbch ?? '',
          timezone: entry.timezone ?? '',
          field: entry.field?.name ?? '',
          total_quantity: product.totalQuantity ?? '',
          quantity_unit: product.quantityUnit ?? '',
          target: entry.target ?? '',
          operator: entry.operator ?? '',
          machine: entry.machine ?? '',
          origin: origin(entry),
          status: entry.status,
          confirmed_at: confirmedAt,
        };
      }),
    );
}
const CSV_FORMULA_PREFIX = /^[=+\-@\t\r]/;

export function buildCsv(rows: ExportRow[], titles: string[]): string {
  const escape = (value: string | number) => {
    let cell = String(value);
    if (typeof value === 'string' && CSV_FORMULA_PREFIX.test(value)) {
      cell = `'${cell}`;
    }
    return `"${cell.replace(/"/g, '""')}"`;
  };
  return (
    '\uFEFF' +
    [titles, ...rows.map((row) => EXPORT_COLUMNS.map((column) => row[column]))]
      .map((row) => row.map(escape).join(','))
      .join('\r\n') +
    '\r\n'
  );
}
export function sumKnown(
  values: Array<number | undefined | null>,
): number | undefined {
  const known = values.filter(
    (value) => typeof value === 'number' && Number.isFinite(value),
  ) as number[];
  if (!known.length) {
    return undefined;
  }
  return known.reduce((sum, value) => sum + value, 0);
}
export function worstStatus(
  entries: FieldNotebookEntry[],
): FieldNotebookStatus {
  if (entries.some((entry) => entry.status === 'pending')) {
    return 'pending';
  }
  if (entries.some((entry) => entry.status === 'ready')) {
    return 'ready';
  }
  return 'confirmed';
}
/**
 * Summarizes the loaded entries and their earliest unconfirmed deadline.
 * @remarks Deadline wording compares local calendar days after entry timezone
 * resolution; partial days and DST changes never alter the number of dates crossed.
 */
export function notebookSummary(
  entries: FieldNotebookEntry[],
  now = Date.now(),
) {
  const pending = entries.filter((entry) => entry.status === 'pending').length;
  const due = entries
    .filter(
      (entry) =>
        entry.status !== 'confirmed' &&
        Number.isFinite(Date.parse(entry.dueAt)),
    )
    .sort((a, b) => Date.parse(a.dueAt) - Date.parse(b.dueAt))[0];
  let days: number | undefined;
  if (due) {
    const dueLocalDay = new Date(
      `${entryDateTime(due.dueAt, due.timezone).date}T00:00:00`,
    );
    days = calendarDay(dueLocalDay) - calendarDay(new Date(now));
  }
  return {
    applications: entries.length,
    products: entries.reduce((sum, entry) => sum + entry.products.length, 0),
    area: sumKnown(entries.map((entry) => entry.treatedAreaHa)),
    pending,
    ready: entries.filter((entry) => entry.status === 'ready').length,
    confirmed: entries.filter((entry) => entry.status === 'confirmed').length,
    due,
    days,
  };
}
export function aggregateFields(
  entries: FieldNotebookEntry[],
  fields: Field[],
) {
  const groups = new Map<string, FieldNotebookEntry[]>();
  entries.forEach((entry) => {
    const key = entry.field?.id ?? '';
    const group = groups.get(key) ?? [];
    group.push(entry);
    groups.set(key, group);
  });
  return [...groups].map(([id, items]) => ({
    id,
    field: fields.find((field) => field.id === id),
    name: items[0].field?.name,
    parcel: items[0].field?.parcelReference,
    crops: [...new Set(items.map((entry) => entry.crop?.name).filter(Boolean))],
    applications: items.length,
    area: sumKnown(items.map((entry) => entry.treatedAreaHa)),
    products: [
      ...new Set(
        items.flatMap((entry) => entry.products.map((product) => product.name)),
      ),
    ],
    last: [...items].sort(
      (a, b) => (Date.parse(b.startedAt) || 0) - (Date.parse(a.startedAt) || 0),
    )[0],
    status: worstStatus(items),
    missing: items.reduce((sum, entry) => sum + entry.missingFields.length, 0),
  }));
}
export function aggregateProducts(entries: FieldNotebookEntry[]) {
  const groups = new Map<
    string,
    { entry: FieldNotebookEntry; product: FieldNotebookProduct }[]
  >();
  productRows(entries).forEach((row) => {
    const key = `${row.product.name.trim().toLocaleLowerCase()}|${row.product.authorizationNumber ?? ''}`;
    const group = groups.get(key) ?? [];
    group.push(row);
    groups.set(key, group);
  });
  return [...groups.values()].map((items) => {
    const unique = [
      ...new Map(items.map((item) => [item.entry.id, item.entry])).values(),
    ];
    const quantities: Record<string, number> = {};
    let missing = 0;
    items.forEach(({ product }) => {
      if (
        Number.isFinite(product.totalQuantity) &&
        (product.quantityUnit === 'L' || product.quantityUnit === 'kg')
      ) {
        quantities[product.quantityUnit] =
          (quantities[product.quantityUnit] ?? 0) + product.totalQuantity;
      } else {
        missing += 1;
      }
    });
    return {
      product: items[0].product,
      applications: unique.length,
      quantities: Object.entries(quantities).map(([unit, value]) => ({
        unit,
        value,
      })),
      missing,
      area: sumKnown(unique.map((entry) => entry.treatedAreaHa)),
      fields: [
        ...new Set(
          unique
            .map((entry) => entry.field?.name || entry.field?.nameOnDevice)
            .filter(Boolean),
        ),
      ],
    };
  });
}
export function panelNotice(status: FieldNotebookStatus): string {
  return `field-notebook.notice-${status}`;
}
const MISSING_KEYS: Record<string, string> = {
  startedAt: 'field-notebook.missing.started-at',
  treatedArea: 'field-notebook.missing.treated-area',
  field: 'field-notebook.missing.field',
  crop: 'field-notebook.missing.crop',
  products: 'field-notebook.missing.products',
  name: 'field-notebook.missing.product-name',
  authorizationNumber: 'field-notebook.missing.authorization',
  dose: 'field-notebook.missing.dose',
};
/** Turns the server missingFields codes (`crop`, `products.0.dose`) into labels. */
export function missingItems(
  missing: string[],
): { key: string; product?: number }[] {
  return missing.map((code) => {
    const [scope, index, attribute] = code.split('.');
    if (scope === 'products' && attribute) {
      return {
        key: MISSING_KEYS[attribute] ?? MISSING_KEYS.products,
        product: Number(index) + 1,
      };
    }
    return { key: MISSING_KEYS[code] ?? MISSING_KEYS.products };
  });
}
